import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  CheckGlyph,
  delay,
  tone,
  useCopyGap,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { useMusicT } from './kit';
import { MUSIC_HOME_PATH } from './links';
import NowPlaying from './NowPlaying';
import SongBox from './SongBox';
import type { MusicLandingPage } from './types';

/**
 * Headline and song box first (people arrive here to make a song), next to
 * a player with real songs made from one prompt each.
 */
export default function MusicHero({ page }: { page: MusicLandingPage }) {
  const { t } = useMusicT();
  const gap = useCopyGap();

  const points = [
    t('Free to try, no credit card'),
    t('Two versions of every song'),
    t('MP3 and WAV downloads'),
  ];

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pb-6 pt-10 sm:pt-14 lg:px-8'>
        <div className='grid gap-x-14 gap-y-12 lg:grid-cols-12 lg:items-center'>
          <div className='min-w-0 lg:col-span-7'>
            {page.id === 'main' ? (
              <p
                className={clsxm(
                  styles.enter,
                  'inline-flex items-center gap-2 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] dark:bg-white/[0.05] dark:ring-white/10',
                  tone.body,
                )}
              >
                <span className='flex-none whitespace-nowrap rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
                  {t('Free to try')}
                </span>
                {t(page.eyebrow)}
              </p>
            ) : (
              <nav
                aria-label={t('Breadcrumb')}
                className={clsxm(styles.enter, 'text-sm font-medium')}
              >
                <ol className='flex flex-wrap items-center gap-2'>
                  <li>
                    <Link
                      href={MUSIC_HOME_PATH}
                      className='text-primary-700 transition-colors hover:text-primary-800 dark:text-primary-300 dark:hover:text-primary-200'
                    >
                      {t('AI Music Generator')}
                    </Link>
                  </li>
                  <li aria-hidden='true' className={tone.quiet}>
                    /
                  </li>
                  <li aria-current='page' className={tone.body}>
                    {t(page.name)}
                  </li>
                </ol>
              </nav>
            )}

            <h1
              className={clsxm(
                styles.display,
                styles.heroTitle,
                styles.enter,
                'mt-6 text-[2.5rem] font-semibold leading-[1.05] sm:text-[3.25rem] sm:leading-[1.04] xl:text-[3.75rem]',
                tone.ink,
              )}
              style={delay(60)}
            >
              {t(page.title)}
              {page.titleTail ? (
                <>
                  {gap}
                  <span className='text-primary-600 dark:text-primary-400'>
                    {t(page.titleTail)}
                  </span>
                </>
              ) : null}
            </h1>

            <p
              className={clsxm(
                styles.enter,
                'mt-6 max-w-2xl text-lg leading-8 sm:text-[1.2rem] sm:leading-[1.65]',
                tone.body,
              )}
              style={delay(140)}
            >
              {t(page.lead)}
            </p>

            <div className={styles.enter} style={delay(200)}>
              <SongBox page={page} />
            </div>

            <ul
              className={clsxm(
                styles.enter,
                'mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-medium',
                tone.body,
              )}
              style={delay(260)}
            >
              {points.map((point) => (
                <li key={point} className='flex items-center gap-2'>
                  <span className='flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'>
                    <CheckGlyph className='h-3 w-3' />
                  </span>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          <div
            className={clsxm(styles.enter, 'min-w-0 lg:col-span-5')}
            style={delay(240)}
          >
            <NowPlaying sounds={page.sounds} />
          </div>
        </div>
      </div>
    </header>
  );
}
